import { c as _c } from "react/compiler-runtime";
import { Locale } from "@/types/locale";
import React, { useMemo } from "react";
import { useTranslations } from "next-intl";
import { ExternalLink } from "@components/common/external-link";
import { contentTools } from "@lib/content";
import { localizeFrom } from "@lib/i18n/utils";
import { useTranslateProposalFields } from "@features/proposals/hooks/proposal-translate-fields";
import { proposalsTools } from "@features/proposals/proposals-tools";
import { ContributorBiographyInlineOutputView } from "@features/proposals/types/member";
import { MemberPhotoView } from "@features/proposals/ui/member/member-photo-view";
import { IconBrandTwitter } from "@tabler/icons-react";
interface MemberBiographyViewBlockProps {
  biography?: ContributorBiographyInlineOutputView | undefined;
  shortMode?: boolean | undefined;
  locale: Locale;
  contactEmail?: string;
}
export const SHORT_MODE_THRESHOLD = 300;
const getLocalizedBiographyText = (value: unknown, locale: Locale): string | undefined => {
  if (typeof value === "string") {
    return value;
  }
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const localizedValue = (value as Record<string, unknown>)[locale];
  return typeof localizedValue === "string" ? localizedValue : undefined;
};
export const MemberBiographyViewBlock = t0 => {
  const $ = _c(109);
  const {
    biography,
    shortMode,
    locale,
    contactEmail
  } = t0;
  const {
    translateFieldLabel
  } = useTranslateProposalFields().activityMember.biography;
  const tContributorContactTypes = useTranslations("features.contributors.domain.contributor.fields.contacts.types");
  const bioData = biography?.content?.data;
  const t1 = bioData?.firstName;
  let t2;
  if ($[0] !== locale || $[1] !== t1) {
    t2 = localizeFrom(t1, locale);
    $[0] = locale;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const firstName = t2;
  const t3 = bioData?.lastName;
  let t4;
  if ($[3] !== locale || $[4] !== t3) {
    t4 = localizeFrom(t3, locale);
    $[3] = locale;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const lastName = t4;
  const t5 = bioData?.alias;
  let t6;
  if ($[6] !== locale || $[7] !== t5) {
    t6 = localizeFrom(t5, locale);
    $[6] = locale;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const alias = t6;
  const t7 = bioData?.company;
  let t8;
  if ($[9] !== locale || $[10] !== t7) {
    t8 = localizeFrom(t7, locale);
    $[9] = locale;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const company = t8;
  const t9 = bioData?.position;
  let t10;
  if ($[12] !== locale || $[13] !== t9) {
    t10 = localizeFrom(t9, locale);
    $[12] = locale;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const position = t10;
  const titulus = bioData?.titulus?.iv;
  const t11 = bioData?.description;
  let t12;
  if ($[15] !== locale || $[16] !== t11) {
    t12 = getLocalizedBiographyText(t11, locale);
    $[15] = locale;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const description = t12;
  let t13;
  if ($[18] !== biography) {
    t13 = proposalsTools.activityMemberTools.messengerActions.makeTwitterLinkFromBio(biography);
    $[18] = biography;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const twitterLink = t13;
  let t14;
  if ($[20] !== description || $[21] !== shortMode) {
    t14 = shortMode && description ? `${description.slice(0, SHORT_MODE_THRESHOLD)}...` : description;
    $[20] = description;
    $[21] = shortMode;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const descriptionText = t14;
  let t15;
  if ($[23] !== biography || $[24] !== firstName || $[25] !== lastName) {
    t15 = <MemberPhotoView biography={biography} firstName={firstName} lastName={lastName} size="lg" />;
    $[23] = biography;
    $[24] = firstName;
    $[25] = lastName;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== translateFieldLabel) {
    t16 = translateFieldLabel("firstName");
    $[27] = translateFieldLabel;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== translateFieldLabel) {
    t17 = translateFieldLabel("lastName");
    $[29] = translateFieldLabel;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t16 || $[32] !== t17) {
    t18 = <span className="text-sm font-medium text-muted-foreground">{t16} {t17}:</span>;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const t19 = firstName || "-";
  let t20;
  if ($[34] !== t19) {
    t20 = <span className="ml-2 font-semibold">{t19}</span>;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  const t21 = lastName || "-";
  let t22;
  if ($[36] !== t21) {
    t22 = <span className="font-semibold">{t21}</span>;
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== t18 || $[39] !== t20 || $[40] !== t22) {
    t23 = <div>{t18}{t20}{" "}{t22}</div>;
    $[38] = t18;
    $[39] = t20;
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== translateFieldLabel) {
    t24 = translateFieldLabel("alias");
    $[42] = translateFieldLabel;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t24) {
    t25 = <span className="text-sm font-medium text-muted-foreground">{t24}:</span>;
    $[44] = t24;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  const t26 = alias || "-";
  let t27;
  if ($[46] !== t26) {
    t27 = <span className="ml-2">{t26}</span>;
    $[46] = t26;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== t25 || $[49] !== t27) {
    t28 = <div>{t25}{t27}</div>;
    $[48] = t25;
    $[49] = t27;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  let t29;
  if ($[51] !== translateFieldLabel) {
    t29 = translateFieldLabel("company");
    $[51] = translateFieldLabel;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[53] !== t29) {
    t30 = <span className="text-sm font-medium text-muted-foreground">{t29}:</span>;
    $[53] = t29;
    $[54] = t30;
  } else {
    t30 = $[54];
  }
  const t31 = company || "-";
  let t32;
  if ($[55] !== t31) {
    t32 = <span className="ml-2">{t31}</span>;
    $[55] = t31;
    $[56] = t32;
  } else {
    t32 = $[56];
  }
  let t33;
  if ($[57] !== t30 || $[58] !== t32) {
    t33 = <div>{t30}{t32}</div>;
    $[57] = t30;
    $[58] = t32;
    $[59] = t33;
  } else {
    t33 = $[59];
  }
  let t34;
  if ($[60] !== translateFieldLabel) {
    t34 = translateFieldLabel("position");
    $[60] = translateFieldLabel;
    $[61] = t34;
  } else {
    t34 = $[61];
  }
  let t35;
  if ($[62] !== t34) {
    t35 = <span className="text-sm font-medium text-muted-foreground">{t34}:</span>;
    $[62] = t34;
    $[63] = t35;
  } else {
    t35 = $[63];
  }
  const t36 = position || "-";
  let t37;
  if ($[64] !== t36) {
    t37 = <span className="ml-2">{t36}</span>;
    $[64] = t36;
    $[65] = t37;
  } else {
    t37 = $[65];
  }
  let t38;
  if ($[66] !== t35 || $[67] !== t37) {
    t38 = <div>{t35}{t37}</div>;
    $[66] = t35;
    $[67] = t37;
    $[68] = t38;
  } else {
    t38 = $[68];
  }
  let t39;
  if ($[69] !== contactEmail || $[70] !== tContributorContactTypes) {
    t39 = contactEmail && <div><span className="text-sm font-medium text-muted-foreground">{tContributorContactTypes("email")}:</span><span className="ml-2 break-all">{contactEmail}</span></div>;
    $[69] = contactEmail;
    $[70] = tContributorContactTypes;
    $[71] = t39;
  } else {
    t39 = $[71];
  }
  let t40;
  if ($[72] !== translateFieldLabel) {
    t40 = translateFieldLabel("titulus");
    $[72] = translateFieldLabel;
    $[73] = t40;
  } else {
    t40 = $[73];
  }
  let t41;
  if ($[74] !== t40) {
    t41 = <span className="text-sm font-medium text-muted-foreground">{t40}:</span>;
    $[74] = t40;
    $[75] = t41;
  } else {
    t41 = $[75];
  }
  const t42 = titulus || "-";
  let t43;
  if ($[76] !== t42) {
    t43 = <span className="ml-2">{t42}</span>;
    $[76] = t42;
    $[77] = t43;
  } else {
    t43 = $[77];
  }
  let t44;
  if ($[78] !== t41 || $[79] !== t43) {
    t44 = <div>{t41}{t43}</div>;
    $[78] = t41;
    $[79] = t43;
    $[80] = t44;
  } else {
    t44 = $[80];
  }
  let t45;
  if ($[81] !== translateFieldLabel) {
    t45 = translateFieldLabel("twitter");
    $[81] = translateFieldLabel;
    $[82] = t45;
  } else {
    t45 = $[82];
  }
  let t46;
  if ($[83] !== t45) {
    t46 = <span className="text-sm font-medium text-muted-foreground">{t45}:</span>;
    $[83] = t45;
    $[84] = t46;
  } else {
    t46 = $[84];
  }
  let t47;
  if ($[85] !== biography || $[86] !== twitterLink) {
    t47 = twitterLink ? <ExternalLink href={twitterLink} className="ml-2"><IconBrandTwitter className="mr-1 inline h-4 w-4" />{proposalsTools.activityMemberTools.extractTwitterData(biography)}</ExternalLink> : <span className="ml-2">-</span>;
    $[85] = biography;
    $[86] = twitterLink;
    $[87] = t47;
  } else {
    t47 = $[87];
  }
  let t48;
  if ($[88] !== t46 || $[89] !== t47) {
    t48 = <div className="md:col-span-2">{t46}{t47}</div>;
    $[88] = t46;
    $[89] = t47;
    $[90] = t48;
  } else {
    t48 = $[90];
  }
  let t49;
  if ($[91] !== t23 || $[92] !== t28 || $[93] !== t33 || $[94] !== t38 || $[95] !== t39 || $[96] !== t44 || $[97] !== t48) {
    t49 = <div className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2">{t23}{t28}{t33}{t38}{t39}{t44}{t48}</div>;
    $[91] = t23;
    $[92] = t28;
    $[93] = t33;
    $[94] = t38;
    $[95] = t39;
    $[96] = t44;
    $[97] = t48;
    $[98] = t49;
  } else {
    t49 = $[98];
  }
  let t50;
  if ($[99] !== t15 || $[100] !== t49) {
    t50 = <div className="flex items-center gap-6">{t15}{t49}</div>;
    $[99] = t15;
    $[100] = t49;
    $[101] = t50;
  } else {
    t50 = $[101];
  }
  let t51;
  if ($[102] !== description || $[103] !== descriptionText || $[104] !== translateFieldLabel) {
    t51 = description && <div className="border-t pt-3"><div className="mb-2 text-sm font-medium text-muted-foreground">{translateFieldLabel("description")}:</div>{descriptionText && contentTools.getSanitizedContent(descriptionText) && <div className="text-sm" dangerouslySetInnerHTML={{
        __html: descriptionText
      }} />}</div>;
    $[102] = description;
    $[103] = descriptionText;
    $[104] = translateFieldLabel;
    $[105] = t51;
  } else {
    t51 = $[105];
  }
  let t52;
  if ($[106] !== t50 || $[107] !== t51) {
    t52 = <div className="flex flex-col gap-4">{t50}{t51}</div>;
    $[106] = t50;
    $[107] = t51;
    $[108] = t52;
  } else {
    t52 = $[108];
  }
  return t52;
};